import { useState } from "react";
import { KfAvatar } from "../knightsfee/KfAvatar";
import { Button } from "../ui/button";
import { ShoppingCart, Check, Lock, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CATEGORY_LABELS = { campaign: "Campaign", map: "Map", token: "Token", npc: "NPC", item: "Item", weapon: "Weapon", music: "Music", sfx: "SFX" };

const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

// One card in the Bazaar's browse grid. Deliberately dumb/presentational — all cart state lives
// in BazaarPage so multiple cards can share one source of truth for "is this already in cart".
export function BazaarListingCard({ listing, inCart, onToggleCart }) {
  const [revealed, setRevealed] = useState(false);
  const [contacting, setContacting] = useState(false);
  const blurred = listing.is_adult && !revealed;

  const contactSeller = async () => {
    setContacting(true);
    try {
      const { data } = await api.get(`/bazaar/listings/${listing.id}/contact`);
      window.location.href = `mailto:${data.contact_email}?subject=${encodeURIComponent(`Call to Arms Bazaar — ${data.listing_title}`)}&body=${encodeURIComponent(`Hi ${data.seller_name},\n\nI'm interested in your listing "${data.listing_title}" and would like to discuss the details.\n\n`)}`;
    } catch {
      toast.error("Couldn't load seller contact info");
    } finally {
      setContacting(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#1c1712] overflow-hidden flex flex-col group hover:border-[#D4AF37]/40 transition-colors duration-200" data-testid={`bazaar-listing-${listing.id}`}>
      <div className="aspect-[4/3] bg-black/40 overflow-hidden relative">
        {listing.image_url ? (
          <img src={listing.image_url} alt={listing.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${blurred ? "blur-lg scale-110" : ""}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#f1e0c5]/20 text-xs uppercase tracking-widest">No Preview</div>
        )}
        {listing.is_custom && <span className="absolute top-2 left-2 bg-black/70 text-[#D4AF37] text-[9px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#D4AF37]/40">Custom Commission</span>}
        {blurred && (
          <button
            type="button"
            data-testid={`bazaar-listing-reveal-adult-${listing.id}`}
            onClick={() => setRevealed(true)}
            className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/30 text-[#f1e0c5] text-[10px] uppercase tracking-widest"
          >
            <Lock className="w-5 h-5 text-[#D4AF37]" />
            Adult Content — Click to Reveal
          </button>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 flex-1">
        <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]/70">{CATEGORY_LABELS[listing.category] || listing.category}</span>
        <h3 className="font-display text-lg leading-tight" data-testid={`bazaar-listing-title-${listing.id}`}>{listing.title}</h3>
        {listing.description && <p className="text-xs text-[#f1e0c5]/50 line-clamp-2">{listing.description}</p>}
        <div className="flex items-center gap-2 mt-auto pt-1">
          <KfAvatar name={listing.seller.name} avatarUrl={listing.seller.avatar_url} isMerchant={listing.seller.is_merchant} size="sm" />
          <span className="text-xs text-[#f1e0c5]/60 truncate">{listing.seller.name}</span>
        </div>
        <div className="flex items-center justify-between pt-1 gap-2">
          <span className="font-display text-xl text-[#D4AF37]" data-testid={`bazaar-listing-price-${listing.id}`}>{formatPrice(listing.price_cents)}</span>
          <div className="flex items-center gap-1.5">
            {listing.is_custom && (
              <Button
                size="sm" variant="secondary" disabled={contacting}
                data-testid={`bazaar-listing-contact-seller-${listing.id}`}
                onClick={contactSeller}
                className="rounded-full text-xs h-8 gap-1.5"
              >
                <MessageCircle className="w-3.5 h-3.5" /> Contact
              </Button>
            )}
            <Button
              size="sm"
              data-testid={`bazaar-listing-cart-toggle-${listing.id}`}
              onClick={() => onToggleCart(listing)}
              variant={inCart ? "secondary" : "default"}
              className={`rounded-full text-xs h-8 gap-1.5 ${inCart ? "" : "bg-[#D90429] hover:bg-[#b8031f]"}`}
            >
              {inCart ? <><Check className="w-3.5 h-3.5" /> In Cart</> : <><ShoppingCart className="w-3.5 h-3.5" /> Add</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export { CATEGORY_LABELS, formatPrice };
